// Subscription-signing facade. Wraps Privy's useSignTypedData and the
// shared @rmint/core/signing/subscription-agreement module so the
// widget code stays thin.
//
// signSubscriptionAgreement is split out as a pure async function
// (taking the signer as a dep) so it's unit-testable without React.

import { keccak256, stringToBytes, type Hex } from 'viem';
import {
  buildTypedData,
  type SubscriptionAgreementInput,
} from '@rmint/core/signing/subscription-agreement';
import { useSignTypedData } from '@privy-io/react-auth';

export type SignSubscriptionInput = {
  subscriptionId: string;
  investor: `0x${string}`;
  restaurantId: string;
  amountUsdc: number; // USDC dollars (will be converted to micro-USDC for the typed data)
  tokens: number;
  pricePerToken: number; // USDC dollars per token
  lockupMonths: number;
  nonce: number; // = state.current.version at sign-time
  chainId: number;
  saleContractAddress: `0x${string}`;
  agreementText: string; // canonical legal text from offering.yaml
};

export type SignSubscriptionOutput = {
  signature: Hex;
  agreementHash: Hex;
  signedAt: string;
};

// SignerLike matches Privy's useSignTypedData return shape:
//   signTypedData(input) => Promise<{ signature: string }>
// We accept `unknown` input so the function is testable with a mock.
type SignerLike = {
  signTypedData: (input: unknown) => Promise<{ signature: string } | `0x${string}`>;
};

export async function signSubscriptionAgreement(
  signer: SignerLike,
  input: SignSubscriptionInput,
): Promise<SignSubscriptionOutput> {
  const termsHash = keccak256(stringToBytes(input.agreementText));
  const moduleInput: SubscriptionAgreementInput = {
    subscriptionId: input.subscriptionId,
    investor: input.investor,
    restaurantId: input.restaurantId,
    amountUsdc: BigInt(input.amountUsdc) * 1_000_000n,
    tokens: BigInt(input.tokens),
    pricePerToken: BigInt(Math.round(input.pricePerToken * 1_000_000)),
    lockupMonths: BigInt(input.lockupMonths),
    termsHash,
    nonce: BigInt(input.nonce),
    chainId: input.chainId,
    saleContractAddress: input.saleContractAddress,
  };
  const typedData = buildTypedData(moduleInput);
  // Privy's SignRequestScreen calls JSON.stringify(message) to render
  // the typed-data preview to the user. JSON.stringify chokes on bigint
  // ("Do not know how to serialize a BigInt"), blanking the entire
  // page. Convert uint256 fields to decimal strings before handing
  // typed data to Privy. viem's hashTypedData accepts either bigint or
  // string for uint* fields and produces the same digest, so the
  // server-side signature verification (which builds typedData with
  // bigints via @rmint/core/signing/subscription-agreement#buildTypedData)
  // still recovers the signer correctly.
  const displaySafeTypedData = {
    ...typedData,
    message: {
      ...typedData.message,
      amountUsdc: typedData.message.amountUsdc.toString(),
      tokens: typedData.message.tokens.toString(),
      pricePerToken: typedData.message.pricePerToken.toString(),
      lockupMonths: typedData.message.lockupMonths.toString(),
      nonce: typedData.message.nonce.toString(),
    },
  };
  // Cast: SignerLike is intentionally loose to accommodate Privy + tests.
  // The runtime shape (string-valued uint256s) is valid EIP-712 input.
  const result = await signer.signTypedData(displaySafeTypedData as Parameters<typeof signer.signTypedData>[0]);
  // Privy returns { signature: string }; test mocks may return a plain Hex string.
  const signature = (typeof result === 'string' ? result : result.signature) as Hex;
  return {
    signature,
    agreementHash: termsHash,
    signedAt: new Date().toISOString(),
  };
}

export type UseSubscriptionSignerResult = {
  signSubscriptionAgreement: (input: SignSubscriptionInput) => Promise<SignSubscriptionOutput>;
};

export function useSubscriptionSigner(): UseSubscriptionSignerResult {
  const { signTypedData } = useSignTypedData();
  // Privy's signTypedData is strongly typed to SignTypedDataParams; we cast
  // to SignerLike so signSubscriptionAgreement can pass the viem typed-data
  // object through without importing Privy's param types in the pure layer.
  const signer: SignerLike = { signTypedData: signTypedData as SignerLike['signTypedData'] };
  return {
    signSubscriptionAgreement: (input) => signSubscriptionAgreement(signer, input),
  };
}
